//
// Copyright 2025 DXOS.org
//

import * as Context from 'effect/Context';
import * as Effect from 'effect/Effect';
import * as Layer from 'effect/Layer';
import * as Option from 'effect/Option';
import type * as Atom from 'effect/reactivity/Atom';
import type * as Scope from 'effect/Scope';

import type { DXN } from '@dxos/keys';

import type * as ActivationEvent from './activation-event.ts';
import type * as CapabilityManager from './capability-manager.ts';
import { CapabilityNotFoundError } from './errors.ts';
import type * as Plugin from './plugin.ts';

//
// Capability Service Layer
//

/**
 * Effect Context.Tag for accessing CapabilityManager via the Effect layer system.
 * This allows capability modules to access the capability manager without having it passed as an argument.
 */
export class Service extends Context.Service<Service, CapabilityManager.CapabilityManager>()(
  '@dxos/app-framework/CapabilityManager',
) {}

/**
 * Module id of the activation currently executing — set by the loader around each module's
 * `activate` so instrumentation inside module bodies (e.g. {@link lazyModule}'s chunk-import
 * timing) can attribute itself to the module without threading the id through every body.
 */
export const CurrentModuleId: Context.Reference<string | undefined> = Context.Reference<string | undefined>(
  '@dxos/app-framework/Capability/CurrentModuleId',
  { defaultValue: () => undefined },
);

/**
 * Every module id whose `activate` body is on the current fiber's call stack, not just the
 * innermost — a body may fire an activation event, and that event's wave must not wait on a module
 * it is running inside. Such a wait only ends at the activation timeout, which the failure
 * supervisor reads as a broken plugin and disables.
 */
export const ActivatingModuleIds: Context.Reference<ReadonlySet<string>> = Context.Reference<ReadonlySet<string>>(
  '@dxos/app-framework/Capability/ActivatingModuleIds',
  { defaultValue: () => new Set<string>() },
);

/**
 * Get a single capability from the capability manager.
 * @param interfaceDef The interface definition of the capability.
 * @returns The capability implementation.
 * @throws If no capability is found.
 */
export const get = <T>(interfaceDef: InterfaceDef<T>): Effect.Effect<T, CapabilityNotFoundError, Service> =>
  Effect.flatMap(Service, (manager) =>
    Effect.try({
      try: () => manager.get(interfaceDef),
      catch: (error) =>
        error instanceof CapabilityNotFoundError
          ? error
          : new CapabilityNotFoundError({ identifier: interfaceDef.identifier, cause: error }),
    }),
  );

/**
 * Get all capabilities from the capability manager for a given interface.
 * @param interfaceDef The interface definition of the capability.
 * @returns An array of capability implementations.
 */
export const getAll = <T>(interfaceDef: InterfaceDef<T>): Effect.Effect<T[], never, Service> =>
  Effect.map(Service, (manager) => manager.getAll(interfaceDef));

/**
 * Get a single capability from the capability manager, if one is contributed.
 * Never fails — use this over {@link get} when the capability's absence (e.g. an optional plugin
 * isn't loaded) is a legitimate case to handle rather than an error.
 * @param interfaceDef The interface definition of the capability.
 * @returns The first capability implementation, or `Option.none()` if none is contributed.
 */
export const getOption = <T>(interfaceDef: InterfaceDef<T>): Effect.Effect<Option.Option<T>, never, Service> =>
  Effect.map(getAll(interfaceDef), (all) => Option.fromNullishOr(all[0]));

/**
 * Wait for a capability to be available.
 * @param interfaceDef The interface definition of the capability.
 * @returns The capability implementation once available.
 */
export const waitFor = <T>(interfaceDef: InterfaceDef<T>): Effect.Effect<T, never, Service> =>
  Effect.flatMap(Service, (manager) => manager.waitFor(interfaceDef));

/**
 * Get the live contributions view for a multi capability.
 * @param capability The multi capability tag.
 * @returns The live, reactive {@link Contributions} collection.
 */
export const contributions = <T>(
  capability: MultiTag<T, any> | InterfaceDef<T>,
): Effect.Effect<Contributions<T>, never, Service> =>
  Effect.map(Service, (manager) => manager.contributions(capability));

/**
 * Get the Atom reference to capabilities for reactive access.
 * @param interfaceDef The interface definition of the capability.
 * @returns An Atom containing the array of capability implementations.
 */
export const atom = <T>(interfaceDef: InterfaceDef<T>): Effect.Effect<Atom.Atom<T[]>, never, Service> =>
  Effect.map(Service, (manager) => manager.atom(interfaceDef));

/**
 * Get capabilities grouped by the module that contributed them.
 * @param interfaceDef The interface definition of the capability.
 * @returns An Atom containing a record from module ID to capability implementations.
 */
export const atomByModule = <T>(
  interfaceDef: InterfaceDef<T>,
): Effect.Effect<Atom.Atom<Record<string, T[]>>, never, Service> =>
  Effect.map(Service, (manager) => manager.atomByModule(interfaceDef));

/**
 * Constructs a layer that will request its interface implementation from the capability manager.
 */
export const asLayer = <T, I>(interfaceDef: InterfaceDef<T>, tag: Context.Key<I, T>): Layer.Layer<I, never, Service> =>
  Layer.effect(tag, get(interfaceDef).pipe(Effect.orDie));

/**
 * Constructs a layer from a capability by resolving it from the capability manager and passing the
 * value to `build`. Lets a consumer depend on a canonical service (e.g. `Identity.Service`) and
 * have it provided from a capability without the consumer referencing the capability system:
 *
 * ```ts
 * effect.pipe(
 *   Effect.provide(Capability.layerWith(ClientCapabilities.IdentityService, (service) =>
 *     Layer.succeed(Identity.Service, service),
 *   )),
 * );
 * ```
 */
export const layerWith = <T, A, E, R>(
  interfaceDef: InterfaceDef<T>,
  build: (value: T) => Layer.Layer<A, E, R>,
): Layer.Layer<A, E, R | Service> => Layer.unwrap(get(interfaceDef).pipe(Effect.orDie, Effect.map(build)));

const InterfaceDefTypeId: unique symbol = Symbol.for('InterfaceDefTypeId');

/**
 * Symbol used to tag lazy capability functions with their module ID.
 */
export const ModuleTag: unique symbol = Symbol.for('@dxos/app-framework/ModuleTag');

/**
 * The interface definition of a capability.
 */
export type InterfaceDef<T> = {
  [InterfaceDefTypeId]: T;
  identifier: string;
};

export namespace InterfaceDef {
  export type Implementation<I extends InterfaceDef<any>> = I extends InterfaceDef<infer T> ? T : never;
}

//
// Capability tags
//

/**
 * Arity of a capability. `single` capabilities have exactly one provider and yield the
 * implementation directly; `multi` capabilities are open registries contributed to by many
 * modules and yield a live {@link Contributions} collection.
 */
export type Arity = 'single' | 'multi';

declare const CapabilityBrand: unique symbol;

/**
 * Phantom identifier of a capability in the Effect context.
 * Branded by the capability's NSID (a string literal) and arity — never by the service type —
 * so the requirement channel names only local string literals, keeping module bodies'
 * declaration emit portable without per-file annotations. This mirrors Effect's own tags, whose
 * requirement identity is a nominal token (the `Self` class of `Context.Tag`/`Effect.Service`),
 * never the structural service shape. Arity keeps singleton and multi capabilities from crossing APIs.
 */
export interface CapabilityIdentifier<Id extends string, A extends Arity> {
  readonly [CapabilityBrand]: { readonly id: Id; readonly arity: A };
}

/**
 * Live, reactive view over all contributions to a multi capability.
 * The collection is not a barrier: entries may be added or removed as plugins are
 * enabled/disabled — consume reactively.
 */
export interface Contributions<T> {
  /**
   * Reactive atom of the current contributions, for Atom-based composition (e.g. a reactive
   * `OperationHandlerSet`). The manager memoizes one atom per capability, so this is the same
   * instance {@link atom} returns for the same capability.
   */
  readonly atom: Atom.Atom<T[]>;

  /**
   * Snapshot of the current values.
   */
  get(): readonly T[];

  /**
   * Subscribe to changes.
   * @returns Unsubscribe function.
   */
  subscribe(cb: (values: readonly T[]) => void): () => void;
}

/**
 * A singleton capability: a yieldable Effect tag (`yield* tag` yields the implementation)
 * that is also a legacy {@link InterfaceDef}, so string-keyed helpers and hooks accept it
 * unchanged. The tag's runtime `key` equals the capability `identifier`.
 */
// `S` defaults to `any` (not `string`): `Context.Tag` is invariant in its identifier, so a bare
// annotation like `Tag<Example>` — meaning "a singleton tag of this service type, any NSID" —
// must be assignable from every concrete `Tag<Example, "the.actual.nsid">`; defaulting to `string`
// would reject all of them since neither is a subtype of the other under invariance.
export interface Tag<T, S extends string = any>
  extends Context.Key<CapabilityIdentifier<S, 'single'>, T>, InterfaceDef<T> {
  readonly arity: 'single';
}

/**
 * A multi (registry) capability: `yield* tag` yields the live {@link Contributions} view.
 */
export interface MultiTag<T, S extends string = any>
  extends Context.Key<CapabilityIdentifier<S, 'multi'>, Contributions<T>>, InterfaceDef<T> {
  readonly arity: 'multi';
}

/**
 * Either arity of capability tag. One interface rather than a union of the two tag types: in a
 * constraint position `missingEffectContext` reads the union as an Effect and reports one
 * constituent's identifier as a missing service.
 */
export interface AnyTag extends Context.Key<CapabilityIdentifier<any, Arity>, any>, InterfaceDef<any> {
  readonly arity: Arity;
}

/**
 * Compile-time error surfaced when the service type is omitted from the curried factory form.
 * Mirrors Effect's `MissingSelfGeneric` guard on `Effect.Service`/`Context.Tag`: the service type
 * cannot be inferred (it is unrelated to the identifier string), so omitting it would silently
 * widen the identifier to `string` and make the requirement check vacuous — this turns that
 * footgun into a message instead.
 */
type MissingServiceType = 'Missing service type — write make<T>()(nsid) / makeSingleton<T>()(nsid)';

/** Identifier-string parameter, validated as a well-formed NSID at compile time via {@link DXN.Name}. */
type NsidParam<S extends string> = [DXN.Name<S>] extends [never]
  ? `Invalid NSID "${S}": final segment must be camelCase (no hyphens)`
  : S;

// Runtime tag construction — the phantom brand (`CapabilityIdentifier`) and `InterfaceDef` member
// are type-only, so assembling the concrete tag object requires a controlled cast at this boundary
// (as Effect's own tag constructors do internally). Isolated here so call sites stay cast-free.
const buildTag = <T, S extends string, A extends Arity>(identifier: S, arity: A) =>
  Object.assign(Context.Service<CapabilityIdentifier<S, A>, T>(identifier), {
    identifier,
    arity,
  }) as unknown as A extends 'multi' ? MultiTag<T, S> : Tag<T, S>;

/**
 * Defines a multi (registry) capability — the default: an open registry contributed to by
 * many modules, yielding a live {@link Contributions} collection.
 *
 * Preferred (curried) form captures the NSID string literal, so the requirement channel stays
 * precise and portable — the same shape Effect uses for `Context.Tag`/`Effect.Service`:
 * `make<T>()('org.dxos…capability')`. The service type `T` is explicit (it cannot be inferred);
 * the NSID `S` is inferred as a literal in the second call.
 *
 * The single-call form `make<T>(nsid)` is a transitional shim that widens `S` to `string`
 * (identifier stays portable but the requirement check for that capability is coarse); migrate
 * to the curried form to restore a precise check. Static NSID strings are validated via {@link DXN.Name}.
 */
export const make: {
  <T = never>(): [T] extends [never]
    ? MissingServiceType
    : <const S extends string>(identifier: NsidParam<S>) => MultiTag<T, S>;
  <T, S extends string = string>(identifier: NsidParam<S>): MultiTag<T, string>;
} = ((identifier?: string) =>
  identifier === undefined
    ? <const S extends string>(id: S) => buildTag<unknown, S, 'multi'>(id, 'multi')
    : buildTag<unknown, string, 'multi'>(identifier, 'multi')) as any;

/**
 * Defines a singleton capability: exactly one provider, `yield*` yields the implementation.
 * Curried like {@link make} (and Effect's `Context.Tag`/`Effect.Service`): `makeSingleton<T>()(nsid)`;
 * the single-call `makeSingleton<T>(nsid)` is the same transitional shim. Validated via {@link DXN.Name}.
 */
export const makeSingleton: {
  <T = never>(): [T] extends [never]
    ? MissingServiceType
    : <const S extends string>(identifier: NsidParam<S>) => Tag<T, S>;
  <T, S extends string = string>(identifier: NsidParam<S>): Tag<T, string>;
} = ((identifier?: string) =>
  identifier === undefined
    ? <const S extends string>(id: S) => buildTag<unknown, S, 'single'>(id, 'single')
    : buildTag<unknown, string, 'single'>(identifier, 'single')) as any;

/**
 * A unique string identifier with a Typescript type associated with it.
 * When a capability is contributed to the application an implementation of the interface is provided.
 */
export type Capability<T> = {
  /**
   * The interface definition of the capability.
   */
  readonly interface: InterfaceDef<T>;

  /**
   * The implementation of the capability.
   */
  readonly implementation: T;
};

export type Any = Capability<any>;

/**
 * Union of the raw capability-entry shapes a module's `activate` may return, accepted alongside
 * typed {@link Contribution}s so the manager can ingest an already-expanded entry (the shape
 * {@link CapabilityManager.contribute} stores) without re-branding it. Authored modules return
 * {@link Contribution}s instead — only those are checked against a declared `provides`.
 */
export type ModuleReturn = void | Any | Any[] | readonly Any[] | [Any, ...Any[]] | readonly [Any, ...Any[]];

//
// Contributions (typed activate returns)
//

/**
 * Brand symbol carried by every {@link Contribution}. Exported only so the manager layer
 * ({@link CapabilityManager.isContribution}) can detect contributions at runtime — not part of
 * the plugin-authoring API.
 * @internal
 */
export const ContributionTypeId: unique symbol = Symbol.for('@dxos/app-framework/Contribution');
export type ContributionTypeId = typeof ContributionTypeId;

/**
 * A single typed contribution returned from a module's activate, branded by the capability's
 * identifier (NSID + arity) rather than its full tag — so a module body's inferred return type
 * names only local string literals, never the (possibly non-portable) service type `T`, keeping
 * declaration emit portable. The value-type safety is enforced at the {@link contribute} call, where
 * `T` is naturally in scope; downstream only the capability identity matters (completeness check).
 * Carries one value for a singleton capability, n values for a multi capability.
 */
// `Id` is a capability identifier (`CapabilityIdentifier<S, A>`) — left unconstrained because
// `Context.Service.Identifier<C>` is opaque to the checker for a generic tag `C` and wouldn't satisfy
// an explicit bound; the default documents the intended shape.
export interface Contribution<Id = CapabilityIdentifier<string, Arity>> {
  readonly [ContributionTypeId]: Id;
  // Erased to the base tag union in the type (the real tag is present at runtime): a precise `C`
  // here would re-leak its service type into every consuming body's declaration emit.
  readonly capability: AnyTag;
  readonly values: readonly unknown[];
}

export type AnyContribution = Contribution;

/**
 * The capability identifier (NSID + arity) of a tag. Extracted from our own {@link Tag}/
 * {@link MultiTag} `S` parameter rather than via Effect's `Context.Service.Identifier`, which — for a
 * generic tag parameter — falls through to its `TagClassShape` branch and yields the raw tag
 * (leaking the service type again). Singleton checked first; a singleton never matches the multi
 * arm and vice versa (the arity brand differs).
 */
export type IdentifierOf<C> =
  C extends Tag<any, infer S>
    ? CapabilityIdentifier<S, 'single'>
    : C extends MultiTag<any, infer S>
      ? CapabilityIdentifier<S, 'multi'>
      : never;

/**
 * Contributes an implementation for a declared capability.
 * Arity-aware: passing a multi capability where a singleton is expected (or vice versa)
 * is rejected by the value parameter type.
 *
 * Teardown belongs to the module's scope, not the contribution: `yield* Effect.addFinalizer(...)`
 * in the module body. One mechanism covers resources that are not contributions, and finalizers
 * run in a defined order relative to each other.
 */
export const contribute: {
  <C extends Tag<any, any>>(
    capability: C,
    implementation: C extends Tag<infer T, any> ? T : never,
  ): Contribution<IdentifierOf<C>>;
  <C extends MultiTag<any, any>>(
    capability: C,
    implementation: C extends MultiTag<infer T, any> ? T : never,
  ): Contribution<IdentifierOf<C>>;
} = <C extends AnyTag>(capability: C, implementation: unknown): Contribution<IdentifierOf<C>> => ({
  // Controlled brand cast: `[ContributionTypeId]` is a type-only identifier brand; at runtime it
  // holds the tag ({@link CapabilityManager.isContribution} checks presence only).
  [ContributionTypeId]: capability as unknown as IdentifierOf<C>,
  capability,
  values: [implementation],
});

/**
 * Contributes multiple entries for a multi capability from a single module.
 */
export const contributeAll = <C extends MultiTag<any, any>>(
  capability: C,
  implementations: C extends MultiTag<infer T, any> ? readonly T[] : never,
): Contribution<IdentifierOf<C>> => ({
  // Controlled brand cast: see {@link contribute}.
  [ContributionTypeId]: capability as unknown as IdentifierOf<C>,
  capability,
  values: implementations,
});

/**
 * What a module's activate may return for a declared provides tuple: either a single contribution
 * or an (unordered) array of them, whose element type rejects undeclared capabilities. A single
 * contribution is the ergonomic form when a module provides exactly one capability; a module that
 * declares several `provides` must return the array (else {@link EnsureProvides} reports the
 * uncovered ones). Completeness is checked by {@link EnsureProvides}; the plugin manager's runtime
 * validation is authoritative for both (it normalizes a single return via
 * {@link CapabilityManager.normalizeActivateResult}).
 */
export type ProvidesReturn<Provides extends readonly AnyTag[]> = Provides extends readonly []
  ? void | ReadonlyArray<never>
  : Contribution<ProvidedIds<Provides>> | ReadonlyArray<Contribution<ProvidedIds<Provides>>>;

/**
 * The capability identifiers of a provides tuple — the provides-side parallel to
 * {@link Requirements}. Contributions and the completeness check compare identities by these
 * (NSID + arity) rather than by the full tag, so neither leaks the service type.
 */
export type ProvidedIds<Provides extends readonly AnyTag[]> = IdentifierOf<Provides[number]>;

/**
 * The capability identifiers covered by an inferred activate return type.
 * The `never` element guard keeps an empty-array return (inferred as `never[]`) from
 * covering every capability via unconstrained inference.
 */
export type CoveredBy<Ret> =
  Ret extends ReadonlyArray<infer Item>
    ? [Item] extends [never]
      ? never
      : Item extends Contribution<infer Id>
        ? Id
        : never
    : Ret extends Contribution<infer Id>
      ? Id
      : never;

/**
 * Completeness constraint for a module's activate: evaluates to `unknown` (no-op) when every
 * declared capability is covered by the return type, otherwise to an unconstructible branded
 * type naming the missing capability identifiers.
 */
export type EnsureProvides<Ret, Provides extends readonly AnyTag[]> = [ProvidedIds<Provides>] extends [CoveredBy<Ret>]
  ? unknown
  : { readonly 'Missing declared capabilities in activate return': Exclude<ProvidedIds<Provides>, CoveredBy<Ret>> };

/**
 * The Effect environment available to a module's activate for a declared requires tuple.
 * The framework services ({@link Service}, Plugin.Service) and the module scope stay ambient.
 */
export type Requirements<Requires extends readonly AnyTag[]> =
  | Context.Service.Identifier<Requires[number]>
  | Service
  | Plugin.Service
  | Scope.Scope;

/**
 * Loader for a spec-carrying lazy module body. The default export's environment is
 * constrained to the declared requires and its return must cover the declared provides.
 */
export type LoadModule<Props, Requires extends readonly AnyTag[], Provides extends readonly AnyTag[]> = () => Promise<{
  default: (props: Props) => Effect.Effect<ProvidesReturn<Provides>, Error, Requirements<Requires>>;
}>;

/**
 * A module body carrying its activation spec as erased runtime values. The spec types are
 * enforced where the body is authored ({@link lazyModule} / {@link inlineModule}) and
 * deliberately absent from this type, so exporting a module never leaks foreign capability
 * types into declaration emit (TS2883). Runtime manager validation is authoritative.
 */
export interface Module<Options = void> {
  (options: Options): Effect.Effect<any, Error, any>;
  // Optional to match the authoring records this flows into: `Plugin.normalizeActivation` is the
  // single boundary where the spec becomes concrete, so defaulting here would only duplicate it.
  readonly requires?: readonly AnyTag[];
  readonly provides: readonly AnyTag[];
  readonly activatesOn?: ActivationEvent.Events;
  readonly environments?: readonly Environment[];
}

/**
 * A package.json export/import condition a module loads under, via {@link ModuleSpec}'s
 * `environments` — `'browser'`, `'tauri'`, `'node'` and `'workerd'` in this repo.
 *
 * Deliberately an open string rather than a union: conditions are defined by whichever build tool
 * resolves the package, so the framework has no business enumerating them (a consumer targeting
 * `deno`, `electron`, or a private condition is equally valid).
 *
 * Every condition is treated alike: the list names each runtime the module loads under, so a browser
 * module that should also reach the desktop app names `'tauri'` too. Omitting `environments` means
 * the module loads under every condition the plugin names.
 *
 * The annotation must be a literal array at the authoring site: barrel generation reads it
 * statically (variants are emitted per condition, since bundlers follow lazy loaders), so a
 * computed value would be invisible to the generator.
 */
export type Environment = string;

/**
 * Spec shared by {@link lazyModule} and {@link inlineModule}: the requires/provides
 * declaration checked at the authoring site, plus the optional event-mode and
 * props-mapping fields carried alongside it.
 */
type ModuleSpec<Provides extends readonly AnyTag[], Requires extends readonly AnyTag[], Props, Options> = {
  readonly requires?: Requires;
  readonly provides: Provides;
  /** Activates when this runtime event fires instead of during the dependency pass. */
  readonly activatesOn?: ActivationEvent.Events;
  /** Maps plugin options to the body's props; omit when they coincide. */
  readonly props?: (options: Options) => Props;
  /** Conditions this module loads under (literal array); omitted means every condition. */
  readonly environments?: readonly Environment[];
};

/**
 * Helper to define a lazily loaded module body with an eager requires/provides spec.
 * The spec is available without importing the chunk (dependency ordering happens before
 * code-splitting resolves) and the loaded default export is type-checked against it. The
 * returned {@link Module} is opaque with respect to requires/provides/props, parameterized
 * only by `Options` — this is what eliminates TS2883 at every call site.
 * The lazy pairing of {@link makeModule}.
 * @param name The export name (e.g., 'AppGraphBuilder') - used to auto-compute module IDs.
 * @param spec The requires/provides declaration, matching the module authoring site.
 * @param loader The lazy loader function.
 */
export const lazyModule = <
  const Provides extends readonly AnyTag[],
  const Requires extends readonly AnyTag[] = readonly [],
  Props = void,
  Options = Props,
>(
  name: string,
  spec: ModuleSpec<Provides, Requires, Props, Options>,
  loader: LoadModule<Props, Requires, Provides>,
): Module<Options> => {
  const lazyFn = (options: Options): Effect.Effect<any, Error, any> =>
    Effect.gen(function* () {
      // Chunk import measured separately from the body: on deferral the import moves to the
      // interaction path wholesale, so its cost has its own axis in the startup profile.
      const moduleId = (yield* CurrentModuleId) ?? name;
      performance.mark(`module-import:${moduleId}:start`);
      const { default: getModule } = yield* Effect.promise(() => loader());
      performance.mark(`module-import:${moduleId}:end`);
      performance.measure(
        `module-import:${moduleId}`,
        `module-import:${moduleId}:start`,
        `module-import:${moduleId}:end`,
      );
      // Correlation cast: when `spec.props` is absent, `Options` resolves to its `Props`
      // default, so `options` is a valid `Props` value.
      const props = spec.props ? spec.props(options) : (options as unknown as Props);
      return yield* getModule(props);
    });

  return Object.assign(lazyFn, {
    [ModuleTag]: name,
    requires: spec.requires,
    provides: spec.provides,
    activatesOn: spec.activatesOn,
    environments: spec.environments,
  });
};

/**
 * Helper to define an eager module body with the same spec-carrying shape as
 * {@link lazyModule} — for bodies that are plain values (translations, schema) or too small
 * to justify a chunk, so plugin definitions stay a uniform chain of spec-carrying modules.
 * @param name The module name — used to auto-compute module IDs.
 * @param spec The requires/provides declaration.
 * @param activate The module body.
 */
export const inlineModule = <
  const Provides extends readonly AnyTag[],
  const Requires extends readonly AnyTag[] = readonly [],
  Props = void,
  Options = Props,
>(
  name: string,
  spec: ModuleSpec<Provides, Requires, Props, Options>,
  activate: (props: Props) => Effect.Effect<ProvidesReturn<Provides>, Error, Requirements<Requires>>,
): Module<Options> => {
  const body = (options: Options): Effect.Effect<any, Error, any> => {
    // Correlation cast: when `spec.props` is absent, `Options` resolves to its `Props`
    // default, so `options` is a valid `Props` value.
    const props = spec.props ? spec.props(options) : (options as unknown as Props);
    return activate(props);
  };
  return Object.assign(body, {
    [ModuleTag]: name,
    requires: spec.requires,
    provides: spec.provides,
    activatesOn: spec.activatesOn,
    environments: spec.environments,
  });
};

/**
 * Options accepted by makers built with {@link moduleMaker}.
 */
export type MakerOptions<
  Requires extends readonly AnyTag[] = readonly [],
  Extra extends readonly AnyTag[] = readonly [],
  Props = void,
  Options = Props,
> = {
  /** Overrides the default module name (used to derive the module id). */
  name?: string;
  /** Capabilities the body accesses via `yield*`. */
  requires?: Requires;
  /** Additional capabilities the body contributes beyond the maker's default. */
  provides?: Extra;
  /** Activates when this runtime event fires instead of during the dependency pass. */
  activatesOn?: ActivationEvent.Events;
  /** Maps plugin options to the body's props; omit when they coincide. */
  props?: (options: Options) => Props;
  /** Conditions this module loads under (literal array); omitted means every condition. */
  environments?: readonly Environment[];
};

/**
 * Builds a lazy-module maker for a capability, with the tag and default module name baked
 * in so the maker takes only a loader in the common case. Capability owners export makers
 * so consumers author modules without restating the spec.
 *
 * `defaults.activatesOn` sets the family's default demand gate: modules made by the maker are
 * event-mode on it unless the call site declares its own `activatesOn`. This is how a capability
 * owner makes the well-behaved activation the default for every provider (e.g. operation
 * handlers park until an operation is invoked) — startup is not assumed.
 *
 * `defaults.environments` does the same for the runtime axis: a family declares the conditions its
 * modules load under once here (every runtime for schema and operation handlers, `browser` and
 * `tauri` for UI) rather than making each plugin repeat them. The call site's own `environments`
 * still wins.
 */
export const moduleMaker =
  <C extends AnyTag>(
    defaultName: string,
    capability: C,
    defaults?: { activatesOn?: ActivationEvent.Events; environments?: readonly Environment[] },
  ) =>
  <
    Props = void,
    Options = Props,
    const Requires extends readonly AnyTag[] = readonly [],
    const Extra extends readonly AnyTag[] = readonly [],
  >(
    loader: LoadModule<Props, Requires, readonly [C, ...Extra]>,
    options?: MakerOptions<Requires, Extra, Props, Options>,
  ): Module<Options> => {
    // Correlation cast: `provides` is required, so the spread needs a concrete tuple; when
    // options are absent `Extra` resolves to its `readonly []` default, making it the correct
    // value. `requires` needs no such fallback — it stays optional all the way to the boundary.
    const extra = (options?.provides ?? []) as Extra;
    return lazyModule(
      options?.name ?? defaultName,
      {
        requires: options?.requires,
        provides: [capability, ...extra],
        activatesOn: options?.activatesOn ?? defaults?.activatesOn,
        props: options?.props,
        environments: options?.environments ?? defaults?.environments,
      },
      loader,
    );
  };

/**
 * Gets the module tag (export name) from a lazy capability function.
 * @param capability The lazy capability function.
 * @returns The module tag if present, undefined otherwise.
 */
// TODO(wittjosiah): Stricter type for capability.
export const getModuleTag = (capability: unknown): string | undefined => {
  return capability && typeof capability === 'function' && ModuleTag in capability
    ? String(capability[ModuleTag])
    : undefined;
};

/**
 * Helper to define a capability module with explicit typing.
 * Wraps the default export function to provide better type inference and make the pattern explicit.
 *
 * This helper provides explicit typing for the module activation function,
 * making it clear that the function should:
 * - Access declared `requires` via `yield*` (or the ambient `Capability.Service`/`Plugin.Service`)
 * - Return an array of typed {@link Contribution}s (see {@link contribute}/{@link contributeAll})
 *
 * @example
 * ```ts
 * // Module without options - single capability
 * export default Capability.makeModule(
 *   Effect.fnUntraced(function* () {
 *     const client = yield* ClientCapabilities.Client;
 *     return Capability.contribute(Capabilities.SettingsStore, store);
 *   })
 * );
 *
 * // Module with multiple capabilities
 * export default Capability.makeModule(
 *   Effect.fnUntraced(function* () {
 *     return [
 *       Capability.contribute(Capabilities.SettingsStore, store),
 *       Capability.contribute(Capabilities.Translations, translations),
 *     ];
 *   })
 * );
 *
 * // Module with required options (context accessed via layer)
 * export default Capability.makeModule(
 *   Effect.fnUntraced(function* (props: { observability: boolean }) {
 *     const invoker = yield* Capabilities.OperationInvoker;
 *     return Capability.contribute(Capabilities.OperationHandler, ...);
 *   })
 * );
 *
 * // Module with scoped resources (closed automatically on deactivation)
 * export default Capability.makeModule(
 *   Effect.fnUntraced(function* () {
 *     const scope = yield* Scope.Scope;
 *     yield* Scope.addFinalizer(scope, Effect.sync(() => cleanup()));
 *     return Capability.contribute(Capabilities.MyCapability, implementation);
 *   })
 * );
 * ```
 */
export const makeModule = <
  TProps = void,
  TReturn extends AnyContribution | readonly AnyContribution[] = readonly AnyContribution[],
  E extends Error = Error,
  R extends Requirements<readonly AnyTag[]> = Service,
>(
  fn: (props: TProps) => Effect.Effect<TReturn, E, R | Scope.Scope>,
): ((props: TProps) => Effect.Effect<TReturn, E, R | Scope.Scope>) => fn;

export { CapabilityNotFoundError as NotFoundError } from './errors.ts';
